
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  Briefcase, 
  DollarSign, 
  Timer, 
  TrendingUp, 
  Award, 
  Users, 
  Shield, 
  Wallet,
  ChevronUp,
  ChevronDown,
  ExternalLink,
  BarChart3,
  CircleUser
} from "lucide-react";
import { cn } from "@/lib/utils";

interface DashboardStatsProps {
  totalEarnings?: number;
}

// Added new component for individual stat cards with hover effects
const StatCard = ({ 
  title, 
  value, 
  icon, 
  change, 
  trend, 
  color = "blue",
  onClick
}: { 
  title: string;
  value: string;
  icon: React.ReactNode;
  change: string;
  trend: "up" | "down" | "neutral";
  color?: "blue" | "emerald" | "purple" | "amber" | "red";
  onClick?: () => void;
}) => {
  const [isHovered, setIsHovered] = useState(false);
  
  const colorClasses = {
    blue: "bg-blue-50 text-blue-700 border-blue-200",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    purple: "bg-purple-50 text-purple-700 border-purple-200",
    amber: "bg-amber-50 text-amber-700 border-amber-200",
    red: "bg-red-50 text-red-700 border-red-200"
  };
  
  const iconColorClasses = {
    blue: "text-blue-500",
    emerald: "text-emerald-500", 
    purple: "text-purple-500",
    amber: "text-amber-500",
    red: "text-red-500"
  };

  return (
    <Card 
      className={cn(
        "overflow-hidden transition-all duration-300 transform hover:shadow-lg border-2",
        isHovered ? "scale-[1.02]" : "",
        onClick ? "cursor-pointer" : ""
      )}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={onClick}
    >
      <div className={cn("h-1", {
        "bg-blue-500": color === "blue",
        "bg-emerald-500": color === "emerald",
        "bg-purple-500": color === "purple",
        "bg-amber-500": color === "amber",
        "bg-red-500": color === "red",
      })}></div>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-sm font-medium text-gray-500 flex items-center gap-2">
          {title}
          {isHovered && <ExternalLink className="w-3 h-3 text-gray-400" />}
        </CardTitle>
        <div className={cn("p-2 rounded-full", {
          "text-blue-500 bg-blue-50": color === "blue",
          "text-emerald-500 bg-emerald-50": color === "emerald",
          "text-purple-500 bg-purple-50": color === "purple",
          "text-amber-500 bg-amber-50": color === "amber",
          "text-red-500 bg-red-50": color === "red",
        })}>
          {icon}
        </div>
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value}</div>
        <p className={`text-xs flex items-center gap-1 mt-1 ${
          trend === 'up' 
            ? 'text-emerald-500' 
            : trend === 'down' 
            ? 'text-red-500' 
            : 'text-gray-500'
        }`}>
          {trend === 'up' && <ChevronUp className="w-4 h-4" />}
          {trend === 'down' && <ChevronDown className="w-4 h-4" />}
          {change}
        </p>
      </CardContent>
    </Card>
  );
};

const DashboardStats = ({ totalEarnings = 24500 }: DashboardStatsProps) => {
  const [displayMode, setDisplayMode] = useState<"all" | "financial" | "performance">("all");
  
  const stats = [
    {
      title: "Active Projects",
      value: "12",
      icon: <Briefcase className="w-5 h-5" />,
      change: "+2 this week",
      trend: "up" as const,
      category: "performance",
      color: "blue" as const
    },
    {
      title: "Total Earnings",
      value: `$${totalEarnings.toLocaleString()}`,
      icon: <DollarSign className="w-5 h-5" />,
      change: "+15% vs last month",
      trend: "up" as const,
      category: "financial",
      color: "emerald" as const
    },
    {
      title: "Hours Logged",
      value: "164",
      icon: <Timer className="w-5 h-5" />,
      change: "This month",
      trend: "neutral" as const,
      category: "performance",
      color: "blue" as const
    },
    {
      title: "Success Rate",
      value: "94%",
      icon: <TrendingUp className="w-5 h-5" />,
      change: "+2% vs last month",
      trend: "up" as const,
      category: "performance",
      color: "emerald" as const
    },
    {
      title: "Crypto Balance",
      value: "2,450 DGK",
      icon: <Wallet className="w-5 h-5" />,
      change: "+5% this week",
      trend: "up" as const,
      category: "financial",
      color: "purple" as const
    },
    {
      title: "Client Rating",
      value: "4.9/5.0",
      icon: <Award className="w-5 h-5" />,
      change: "Top 5% Developer",
      trend: "up" as const,
      category: "performance",
      color: "amber" as const
    },
    {
      title: "Team Size",
      value: "8",
      icon: <Users className="w-5 h-5" />,
      change: "+2 new members",
      trend: "up" as const,
      category: "performance",
      color: "blue" as const
    },
    {
      title: "Security Score",
      value: "A+",
      icon: <Shield className="w-5 h-5" />,
      change: "All checks passed",
      trend: "neutral" as const,
      category: "performance",
      color: "emerald" as const
    },
    {
      title: "Connections",
      value: "128",
      icon: <CircleUser className="w-5 h-5" />,
      change: "+12 this month",
      trend: "up" as const,
      category: "performance",
      color: "purple" as const
    },
    {
      title: "Avg. Project Value",
      value: "$4,250",
      icon: <BarChart3 className="w-5 h-5" />,
      change: "+8% vs last quarter",
      trend: "up" as const,
      category: "financial",
      color: "amber" as const
    }
  ];

  const filteredStats = displayMode === "all" 
    ? stats 
    : stats.filter(stat => stat.category === displayMode);

  return (
    <div className="space-y-4 mb-8">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-800">Key Metrics</h2>
        <div className="flex space-x-2">
          <Button 
            variant={displayMode === "all" ? "default" : "outline"} 
            size="sm"
            onClick={() => setDisplayMode("all")}
          >
            All
          </Button>
          <Button 
            variant={displayMode === "financial" ? "default" : "outline"} 
            size="sm"
            onClick={() => setDisplayMode("financial")}
          >
            Financial
          </Button>
          <Button 
            variant={displayMode === "performance" ? "default" : "outline"} 
            size="sm"
            onClick={() => setDisplayMode("performance")}
          >
            Performance
          </Button>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
        {filteredStats.map((stat) => (
          <StatCard 
            key={stat.title} 
            title={stat.title}
            value={stat.value}
            icon={stat.icon}
            change={stat.change}
            trend={stat.trend}
            color={stat.color}
            onClick={() => console.log(`${stat.title} card clicked`)}
          />
        ))}
      </div>
    </div>
  );
};

export default DashboardStats;
